import React, { useContext, useMemo, useReducer, useRef } from 'react';
import { initializeStateContext } from '~/shared/bn-context/bn-state-context';
import { createUseSelectedProps } from '~/shared/bn-context/bn-use-selected-props';
import { initializeStateDispatchWrapperContext } from '~/shared/bn-context/bn-state-dispatch-wrapper';
import _ from 'lodash';

export const initializeContext = () => {
  const StateContext = initializeStateContext();
  const StateDispatchWrapperContext = initializeStateDispatchWrapperContext(StateContext);
  const BnContextProvider = createBnContextProvider(StateContext, StateDispatchWrapperContext);

  return {
    createReducer,
    BnContextProvider,
    useSelectedState: StateContext.useSelectedState,
    useContextState: () => useContext(StateContext.State),
    useContextStateSelector: createUseSelectedProps({ StateContext }),
    useContextDispatch: () => useContext(StateContext.Dispatch),
    useStateDispatchWrapper: StateDispatchWrapperContext.useStateDispatchWrapper,
  };
};

// This action should never be used directly by a reducer handler. It is
// specifically for handling the final set state of async reducer handlers.
const _asyncSetStateInternalDispatchType = '__async-internal-set-state';

const createReducer = (handlers, defaultHandler) => {
  // We need a unique instance of our final reducer fn for every instance of our Context
  // which has access to its own dispatch function to be able to dispatch from within async
  // dispatch handlers.
  return function applyDispatchRef (dispatchRef) {
    return (state, action) => {
      if (action.type === _asyncSetStateInternalDispatchType) {
        return action.newState;
      }

      if (_.has(handlers, action.type)) {
        const initialResponse = handlers[action.type](state, action, dispatchRef.current);
        if (typeof initialResponse?.then === 'function') {
          // If you return a promise it will be awaited and the final result will be set to the current state
          (async () => {
            const newState = await initialResponse;
            if (newState !== undefined) {
              dispatchRef.current({ type: _asyncSetStateInternalDispatchType, newState });
            }
          })();

          return state;
        } else {
          // If the response is not a promise, set it to be the current state
          return initialResponse;
        }
      }

      return defaultHandler(state, action, dispatchRef.current);
    };
  };
};

const createBnContextProvider = (StateContext, StateDispatchWrapperContext) => {
  return function BnContextProvider ({ reducer, initialState, children }) {
    const dispatchRef = useRef();
    // We need to create a new instance of our reducer with a reference to our dispatch fn
    const finalReducer = useMemo(() => reducer(dispatchRef), [reducer]);
    const [state, dispatch] = useReducer(finalReducer, initialState);
    dispatchRef.current = dispatch;

    return (
      <StateContext.Provider state={state} dispatch={dispatch}>
        <StateDispatchWrapperContext.Provider {...{ StateDispatchWrapperContext, StateContext }}>
          {children}
        </StateDispatchWrapperContext.Provider>
      </StateContext.Provider>
    );
  };
};
